/** Precio <-> tick, y la proporcion que pide un rango. Portado de frontend/js/01-ui.js. */

/** price0in1 = cuantos token1 por 1 token0, en unidades humanas. */
export function precioATick(p: number, d0: number, d1: number): number {
  return Math.round(Math.log(p / 10 ** (d0 - d1)) / Math.log(1.0001));
}

export function tickAPrecio(t: number, d0: number, d1: number): number {
  return 1.0001 ** t * 10 ** (d0 - d1);
}

/**
 * Cantidades EN BRUTO de una posicion de Uniswap V3, a partir de su liquidez.
 *
 * Sirve para dos cosas distintas con la misma formula: saber que hay dentro AHORA (con el sqrtP
 * del pool) y saber en que se convertiria si el precio llegara a un borde (pasando ese borde como
 * sqrtP). En un borde la posicion queda entera en UNA de las dos monedas — eso no es un fallo del
 * rango, es como funciona la liquidez concentrada, y es justo lo que hay que enseñar antes de que
 * pase.
 */
export function cantidades(sqrtP: number, sa: number, sb: number, L: number) {
  const sp = Math.min(Math.max(sqrtP, sa), sb);
  return {
    a0: sp < sb ? L * (1 / sp - 1 / sb) : 0,
    a1: sp > sa ? L * (sp - sa) : 0,
  };
}

// Que cuenta como estable vive en `lib/estables.ts`, y solo ahi. Aqui habia una expresion regular
// propia que no conocia las mismas monedas que la lista del puente — ver la nota de ese fichero.
// Se reexporta para no tocar a los seis sitios que ya lo importaban de aqui.
import { esEstable } from "./estables";
export { esEstable };

/**
 * Cuadra un tick al multiplo del spacing de ABAJO. Tiene que ser un suelo DE VERDAD, hacia
 * −infinito, porque es lo que hace `_floorTick` en KeelRebalancer.sol:897.
 *
 *     int24 r = (t / s) * s;
 *     return (t < 0 && r != t) ? r - s : r;
 *
 * `Math.trunc(t / s) * s` NO vale: redondea hacia cero, o sea hacia ARRIBA con ticks negativos —
 * y los ticks son negativos en todos los pares donde el estable es token1, que son casi todos
 * (GME/USDG, WETH/USDG…). En un pool del 1% el spacing es 200, asi que el rango se desplazaba
 * hasta un 2%: la pantalla enseñaba un rango y el contrato abria otro.
 *
 * Y lo mismo en la cuenta del swap inicial: calculada sobre el rango equivocado, deja capital
 * parado hasta que el barrido lo recoge.
 */
export function sueloTick(t: number, spacing: number): number {
  const r = Math.trunc(t / spacing) * spacing;
  return t < 0 && r !== t ? r - spacing : r;
}

/**
 * El precio COMO SE LEE: el del activo en el estable.
 *
 * `price0in1` son token1 por token0, que en un par como USDG/NVDA (estable = token0) da 0,004439
 * NVDA por USDG. Nadie piensa asi: piensa 225 USDG por NVDA. Cuando el estable es token0 hay que
 * invertirlo, y con el se invierte el ORDEN de los bordes del rango — el minimo pasa a ser el
 * maximo.
 */
export function vistaPrecio(price0in1: number, simbolo0: string, simbolo1: string) {
  if (esEstable(simbolo1) || !esEstable(simbolo0)) {
    return { px: price0in1, unidad: simbolo1, activo: simbolo0, invertido: false };
  }
  return {
    px: price0in1 > 0 ? 1 / price0in1 : 0,
    unidad: simbolo0,
    activo: simbolo1,
    invertido: true,
  };
}

/**
 * Semiancho del rango, en ticks, a partir del precio minimo y maximo que eligio el usuario.
 * Acotado como lo exige el contrato: `openPosition` revierte con RangeErr si es menor que el
 * `tickSpacing`, y `setRange` no admite mas de 20.000.
 */
export function semianchoEnTicks(
  precioMin: number,
  precioMax: number,
  d0: number,
  d1: number,
  spacing: number
): number {
  const t = Math.round(
    Math.abs(precioATick(precioMax, d0, d1) - precioATick(precioMin, d0, d1)) / 2
  );
  return Math.min(20000, Math.max(spacing || 10, t));
}

/**
 * Que FRACCION del deposito hay que vender para que el minteo despliegue el maximo capital.
 *
 * No es 50/50: un rango asimetrico respecto al precio pide una proporcion distinta, y lo que sobra
 * se queda parado hasta que el barrido lo recoja. Replica `_twapTick` + el snap del contrato para
 * calcular el rango que va a nacer, y de ahi la proporcion en el precio de ahora.
 */
export function fraccionASwapear(
  sqrtPriceX96: bigint,
  tickTwap: number,
  spacing: number,
  below: number,
  above: number,
  estableEsToken0: boolean
): number {
  const sp = Number(sqrtPriceX96) / 2 ** 96;
  const lo = sueloTick(tickTwap - below, spacing);
  const hi = sueloTick(tickTwap + above, spacing);
  const sa = 1.0001 ** (lo / 2);
  const sb = 1.0001 ** (hi / 2);

  let f0: number;
  if (sp <= sa) f0 = 1;
  else if (sp >= sb) f0 = 0;
  else {
    const precio = sp * sp;
    const v0 = (1 / sp - 1 / sb) * precio;
    const v1 = sp - sa;
    f0 = v0 / (v0 + v1);
  }
  const frac = estableEsToken0 ? 1 - f0 : f0;
  return Math.max(0, Math.min(1, frac));
}
